import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { alertService } from '../../../services/alertService';

const WorkflowEnginePanel = () => {
  const [routingRules, setRoutingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchRoutingRules();
  }, []);

  const fetchRoutingRules = async () => {
    try {
      setLoading(true);
      const data = await alertService?.getRoutingRules();
      setRoutingRules(data);
    } catch (error) {
      console.error('Error fetching routing rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (ruleId, currentStatus) => {
    try {
      await alertService?.updateRoutingRule(ruleId, { isActive: !currentStatus });
      fetchRoutingRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      tco2e_deviation: 'tCO₂e Deviation',
      buffer_pool_alert: 'Buffer Pool',
      reversal_risk: 'Reversal Risk',
      leakage_detection: 'Leakage',
      compliance_violation: 'Compliance',
      threshold_breach: 'Threshold',
      data_quality_issue: 'Data Quality',
      verification_required: 'Verification'
    };
    return labels?.[type] || type;
  };

  const getAgentRoleLabel = (role) => {
    const labels = {
      coordinator: 'Coordinator',
      validator: 'Validator',
      analyst: 'Analyst',
      auditor: 'Auditor',
      specialist: 'Specialist'
    };
    return labels?.[role] || role;
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      critical: 'text-destructive bg-destructive/10',
      high: 'text-warning bg-warning/10',
      medium: 'text-primary bg-primary/10',
      low: 'text-muted-foreground bg-secondary'
    };
    return colors?.[urgency] || 'text-muted-foreground bg-secondary';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground mb-1">Workflow Routing Engine</h2>
          <p className="text-sm text-muted-foreground">Configure automated task routing rules and agent assignments</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Icon name="Plus" size={16} className="mr-2" />
          Create Rule
        </Button>
      </div>

      {/* Routing Rules Matrix */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border bg-secondary/30">
          <h3 className="text-sm font-semibold text-foreground">Active Routing Rules</h3>
          <p className="text-xs text-muted-foreground mt-1">Rules are evaluated in priority order for automatic agent assignment</p>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading routing rules...</p>
          </div>
        ) : routingRules?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="GitBranch" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Routing Rules</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first routing rule to enable automated task assignment</p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Icon name="Plus" size={16} className="mr-2" />
              Create First Rule
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {routingRules?.map(rule => (
              <div key={rule?.id} className="p-4 hover:bg-secondary/30 transition-colors">
                <div className="flex items-start gap-4">
                  {/* Priority Badge */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 border-2 border-primary flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">{rule?.priorityScore}</span>
                    </div>
                  </div>

                  {/* Rule Details */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-base font-semibold text-foreground mb-1">{rule?.ruleName}</h4>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getUrgencyColor(rule?.urgencyLevel)}`}>
                            {rule?.urgencyLevel?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">→</span>
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-accent/10 text-accent">
                            {getAgentRoleLabel(rule?.targetAgentRole)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleRule(rule?.id, rule?.isActive)}
                          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                            rule?.isActive
                              ? 'bg-success/10 text-success hover:bg-success/20' :'bg-secondary text-muted-foreground hover:bg-secondary/80'
                          }`}
                        >
                          {rule?.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>

                    {/* Rule Configuration */}
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Alert Type</p>
                        <p className="text-sm font-medium text-foreground">{getAlertTypeLabel(rule?.alertType)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Condition Type</p>
                        <p className="text-sm font-medium text-foreground capitalize">{rule?.conditionType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Priority Score</p>
                        <p className="text-sm font-medium text-foreground">{rule?.priorityScore}/100</p>
                      </div>
                    </div>

                    {/* Trigger Conditions */}
                    {rule?.triggerConditions && Object.keys(rule?.triggerConditions)?.length > 0 && (
                      <div className="mt-3 p-3 bg-secondary/50 rounded-lg">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Trigger Conditions:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(rule?.triggerConditions)?.map(([key, value]) => (
                            <span key={key} className="text-xs bg-background px-2 py-1 rounded border border-border">
                              <span className="text-muted-foreground">{key}:</span>{' '}
                              <span className="text-foreground font-medium">{String(value)}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Priority Matrix Explanation */}
      <div className="mt-6 bg-accent/5 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">How Routing Works</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              When an alert is created, the system evaluates all active rules matching the alert type and urgency level. 
              The rule with the highest priority score is selected, and the alert is automatically assigned to an available 
              agent with the matching role. If no agents are available, the alert remains in the queue until an agent becomes available.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowEnginePanel;